import type { ReactNode } from 'react';
import { Alert, Button, Flex } from '@mantine/core';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { useTranslationInvestor } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import { SignatureSuccessConfirmation } from '@dealstack/frontend/shared/components/EidEasySigningModal/steps/SignatureSuccessConfirmation/SignatureSuccessConfirmation';
import { PublicSignerType } from '@dealstack/shared/lib/publicSignerType';
import type { PublicSigningInfo } from './usePublicSigningInfo';
import { PublicSigningHeader } from './PublicSigningHeader';
import { StatusScreen, SuccessCard } from './PublicSigningPage.styles';

interface Props {
	signingInfo: PublicSigningInfo;
	documentBlob: Blob | undefined;
	isDocumentLoading: boolean;
	/** Portal-return button when the signer arrived from the programme portal; null otherwise. */
	backToPortalButton: ReactNode;
	/** True when the invite dispatched at signing completion failed — drives the retry. */
	witnessInviteFailed?: boolean;
	isRetryingWitnessInvite?: boolean;
	onRetryWitnessInvite?: () => void;
}

/**
 * The already-signed confirmation screen for the public signing page. Witnesses and certifiers
 * may not download the signed document; witnesses also don't see the session section.
 */
export const PublicSigningSignedScreen = ({
	signingInfo,
	documentBlob,
	isDocumentLoading,
	backToPortalButton,
	witnessInviteFailed = false,
	isRetryingWitnessInvite = false,
	onRetryWitnessInvite,
}: Props) => {
	const tInvestor = useTranslationInvestor();
	const isWitness = signingInfo.signerType === PublicSignerType.Witness;
	const canDownload = !isWitness && signingInfo.signerType !== PublicSignerType.Certifier;

	const handleDownloadSignedPdf = () => {
		if (!documentBlob) {
			return;
		}
		const url = window.URL.createObjectURL(documentBlob);
		const link = document.createElement('a');
		link.href = url;
		link.setAttribute('download', `${signingInfo.documentName}.pdf`);
		document.body.appendChild(link);
		link.click();
		link.remove();
		window.URL.revokeObjectURL(url);
	};

	return (
		<Flex direction="column" h="100vh">
			<PublicSigningHeader />
			<StatusScreen direction="column" align="center" justify="center" gap="md">
				{witnessInviteFailed && (
					<Alert
						color="red.9"
						variant="light"
						icon={<Icon variant="warning" size="md" />}
						title={tInvestor('document_workflow.witness.invite_failed')}
					>
						<Button
							data-testid={TEST_ELEMENT_IDS.PUBLIC_WITNESS_INVITE_RETRY_BUTTON}
							variant="outline"
							loading={isRetryingWitnessInvite}
							onClick={onRetryWitnessInvite}
							leftSection={<Icon variant="mail" size="md" />}
						>
							{tInvestor('document_workflow.witness.resend')}
						</Button>
					</Alert>
				)}
				<SuccessCard>
					<SignatureSuccessConfirmation
						signerName={signingInfo.signerName}
						signingMethod={signingInfo.signingMethod}
						signedAt={signingInfo.signedAt}
						session={signingInfo.session}
						showSessionDetails={!isWitness}
						isDownloading={isDocumentLoading}
						onDownloadSignedPdf={canDownload ? handleDownloadSignedPdf : undefined}
					/>
				</SuccessCard>
				{backToPortalButton}
			</StatusScreen>
		</Flex>
	);
};
